import axios from 'axios';
import supabase from './supabaseUtils';

// Get API URL from environment variables
const API_URL = import.meta.env.VITE_STRIPE_API_URL || '/api';

// Force enable mock mode during development if not explicitly set 
// We determine if it's development by checking for localhost or the development environment
const isDevelopment = window.location.hostname === 'localhost' || 
                     window.location.hostname === '127.0.0.1' ||
                     process.env.NODE_ENV === 'development';

// Set to true to always use mock implementation during development
const USE_MOCK_STRIPE = import.meta.env.VITE_USE_MOCK_STRIPE === 'true' || isDevelopment;

console.log('Stripe configuration:', {
  apiUrl: API_URL,
  useMockStripe: USE_MOCK_STRIPE,
  environment: process.env.NODE_ENV,
  isLocalhost: window.location.hostname === 'localhost'
});

/**
 * Creates a payment intent with the Stripe API
 */
export const createPaymentIntent = async (amount, items = []) => {
  try {
    console.log(`Creating PaymentIntent for $${amount} with ${items.length} items`);
    
    const response = await fetch('/api/create-payment-intent', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ amount, items }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Failed to create payment intent: ${response.status} - ${errorData.error || 'Unknown error'}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error creating payment intent:', error);
    throw error;
  }
};

/**
 * Saves order data to the database
 */
export const saveOrder = async (orderData) => {
  try {
    console.log('Saving order data:', { ...orderData, items: orderData.items?.length || 0 });
    
    // Get the authenticated user's ID - necessary for orders table foreign key
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.error('No authenticated user found when trying to save order');
      return { warning: 'No authenticated user found' };
    }
    
    // Create the order record first
    const orderToInsert = {
      user_id: user.id,
      payment_intent_id: orderData.id || orderData.payment_intent_id,
      status: 'completed',
      payment_status: 'paid',
      package_id: orderData.package?.id,
      package_name: orderData.package?.name,
      order_total: orderData.amount || orderData.total,
      requires_shipping: !!orderData.requires_shipping,
      payment_method: 'card',
      event_id: orderData.event_id || null,
      image_count: orderData.items?.length || orderData.image_count || 1,
      created_at: new Date().toISOString()
    };
    
    console.log('Inserting order record:', orderToInsert);
    
    // Insert into orders table
    const { data: orderResult, error: orderError } = await supabase
      .from('orders')
      .insert(orderToInsert)
      .select()
      .single();
      
    if (orderError) {
      console.error('Failed to insert order record:', orderError);
      return { warning: `Order record creation failed: ${orderError.message}` };
    }
    
    console.log('Order record created successfully:', orderResult);
    
    // Now create order items if we have an order and items
    if (orderResult && orderData.items && orderData.items.length > 0) {
      const orderItems = orderData.items.map(item => ({
        order_id: orderResult.id,
        item_type: item.type || 'digital',
        name: item.description || item.name || 'Custom Design',
        description: `Design from order ${orderResult.id}`,
        price: item.price || (orderData.amount / orderData.items.length),
        quantity: 1,
        export_id: item.id, // Assuming item.id is the export_id
        created_at: new Date().toISOString()
      }));
      
      console.log(`Inserting ${orderItems.length} order items`);
      
      const { data: itemsResult, error: itemsError } = await supabase
        .from('order_items')
        .insert(orderItems)
        .select();
        
      if (itemsError) {
        console.error('Failed to insert order items:', itemsError);
        return { 
          success: true, 
          order: orderResult,
          warning: `Order created but items failed: ${itemsError.message}`
        };
      }
      
      console.log(`Successfully inserted ${itemsResult.length} order items`);
      return { 
        success: true, 
        order: orderResult,
        items: itemsResult 
      };
    }
    
    return { success: true, order: orderResult };
  } catch (error) {
    console.error('Error saving order:', error);
    // Don't fail the checkout process if saving the order fails
    return { warning: error.message };
  }
};

/**
 * Saves an order and its items to the database
 * @param {Object} orderData - The order data
 * @param {Array} orderItems - The order items
 * @returns {Promise<Object>} - The created order
 */
export const saveOrderToDatabase = async (orderData, orderItems) => {
  try {
    // Ensure the order has an ID
    if (!orderData.id) {
      orderData.id = crypto.randomUUID();
    }
    
    // Insert order into database
    const { data: order, error: orderError } = await supabase
      .from('orders')
      .insert(orderData)
      .select()
      .single();
      
    if (orderError) {
      throw new Error(`Failed to save order: ${orderError.message}`);
    }
    
    // Process order items if any
    if (orderItems && orderItems.length > 0) {
      // Ensure all items have the order_id
      const itemsWithOrderId = orderItems.map(item => ({
        ...item,
        order_id: orderData.id,
        created_at: item.created_at || new Date().toISOString()
      }));
      
      // Insert items into database
      const { data: items, error: itemsError } = await supabase
        .from('order_items')
        .insert(itemsWithOrderId)
        .select();
        
      if (itemsError) {
        console.error('Error saving order items:', itemsError);
        // We don't throw here to avoid canceling the order if item saving fails
        // Consider implementing a retry mechanism or queue for failed items
      }
      
      // Merge items with the order response
      return { ...order, items };
    }
    
    return order;
  } catch (error) {
    console.error('Database error while saving order:', error);
    throw error;
  }
};

/**
 * Mock implementation for saving orders during development
 * @param {Object} orderData - Order details 
 * @param {Array} orderItems - Array of order item objects
 * @returns {Promise<Object>} - Mocked order response
 */
export const saveOrderMock = async (orderData, orderItems) => {
  console.log('Mock saving order:', orderData);
  console.log('Mock saving order items:', orderItems);
  
  return new Promise(resolve => {
    setTimeout(() => {
      // Create a mock response that mimics the database response
      const mockOrder = {
        ...orderData,
        id: orderData.id || `order_mock_${Math.random().toString(36).substring(2, 15)}`,
        created_at: new Date().toISOString(),
        items: orderItems.map((item, index) => ({
          ...item,
          id: `item_mock_${index}_${Math.random().toString(36).substring(2, 10)}`,
          order_id: orderData.id || `order_mock_${Math.random().toString(36).substring(2, 15)}`
        }))
      };
      
      resolve(mockOrder);
    }, 500);
  });
};

/**
 * Stripe utilities for payment processing
 * 
 * This file contains helper functions for working with Stripe.
 * In production, you should replace the mock implementations with actual Stripe API calls.
 */

/**
 * Simulate a payment intent creation (development only)
 */
function simulatePaymentIntent(amount) {
  // Get or create a stable payment intent ID for the session
  let sessionId = localStorage.getItem('checkout_session_id');
  
  if (!sessionId) {
    // Generate a random ID without any prefix
    sessionId = Math.random().toString(36).substring(2, 15);
    localStorage.setItem('checkout_session_id', sessionId);
  }
  
  // Create a payment intent ID that strictly follows Stripe's format
  // Important: Remove any 'sim_' prefix which causes validation errors
  const paymentIntentId = `pi_${sessionId}`;
  
  // Create a secret value without any prefix
  const secretString = Math.random().toString(36).substring(2, 15);
  
  console.log(`Creating simulated PaymentIntent for $${amount.toFixed(2)}`);
  
  // Format client_secret EXACTLY as Stripe expects: ${id}_secret_${secretString}
  const clientSecret = `${paymentIntentId}_secret_${secretString}`;
  
  console.log(`Generated client secret: ${clientSecret}`);
  
  // For debug - test the regex to verify it matches Stripe's expected format
  const validFormat = /^pi_[a-zA-Z0-9]+_secret_[a-zA-Z0-9]+$/.test(clientSecret);
  console.log(`Client secret format valid: ${validFormat}`);
  
  // Return a consistent payment intent object
  return {
    id: paymentIntentId,
    client_secret: clientSecret,
    amount: Math.round(amount * 100), // Convert to cents
    currency: 'usd',
    status: 'requires_payment_method'
  };
}

/**
 * ==========================================
 * SERVERLESS DEPLOYMENT INFORMATION
 * ==========================================
 * 
 * To deploy this to production, you need a serverless backend to handle Stripe API calls.
 * Here are examples for common serverless platforms:
 * 
 * === VERCEL SERVERLESS FUNCTION ===
 * Create `/api/create-payment-intent.js` file:
 * 
 * ```javascript
 * import Stripe from 'stripe';
 * import { buffer } from 'micro';
 * 
 * const stripe = new Stripe(process.env.STRIPE_SECRET_KEY);
 * 
 * export default async function handler(req, res) {
 *   if (req.method !== 'POST') {
 *     return res.status(405).end('Method Not Allowed');
 *   }
 * 
 *   try {
 *     const { amount, currency = 'usd', metadata } = req.body;
 * 
 *     const paymentIntent = await stripe.paymentIntents.create({
 *       amount,
 *       currency,
 *       metadata,
 *       automatic_payment_methods: { enabled: true }
 *     });
 * 
 *     res.status(200).json({
 *       clientSecret: paymentIntent.client_secret,
 *       id: paymentIntent.id
 *     });
 *   } catch (error) {
 *     res.status(500).json({ error: error.message });
 *   }
 * }
 * 
 * export const config = {
 *   api: {
 *     bodyParser: false,
 *   },
 * };
 * ```
 * 
 * === NETLIFY FUNCTION ===
 * Create `/netlify/functions/create-payment-intent.js` file:
 * 
 * ```javascript
 * const stripe = require('stripe')(process.env.STRIPE_SECRET_KEY);
 * 
 * exports.handler = async function(event, context) {
 *   if (event.httpMethod !== 'POST') {
 *     return { statusCode: 405, body: 'Method Not Allowed' };
 *   }
 * 
 *   try {
 *     const data = JSON.parse(event.body);
 *     const { amount, currency = 'usd', metadata } = data;
 * 
 *     const paymentIntent = await stripe.paymentIntents.create({
 *       amount,
 *       currency,
 *       metadata,
 *       automatic_payment_methods: { enabled: true }
 *     });
 * 
 *     return {
 *       statusCode: 200,
 *       body: JSON.stringify({
 *         clientSecret: paymentIntent.client_secret,
 *         id: paymentIntent.id
 *       })
 *     };
 *   } catch (error) {
 *     return {
 *       statusCode: 500,
 *       body: JSON.stringify({ error: error.message })
 *     };
 *   }
 * };
 * ```
 * 
 * To set up your environment:
 * 
 * 1. Create a `.env.local` file in your project root with:
 *   ```
 *   VITE_STRIPE_PUBLISHABLE_KEY=pk_test_xxxxxxx
 *   VITE_STRIPE_API_URL=/api
 *   VITE_USE_MOCK_STRIPE=false
 *   
 *   # For backend .env (not exposed to browser)
 *   STRIPE_SECRET_KEY=sk_test_xxxxxxx
 *   ```
 * 
 * 2. Remember to add these variables to your hosting environment
 * 
 * 3. Never expose your Stripe secret key in frontend code
 */

/**
 * Mark items as purchased in the database
 * @param {Array} itemIds - Array of item IDs to mark as purchased
 * @returns {Promise} - Result of the update operation
 */
export const markItemsAsPurchased = async (itemIds) => {
  try {
    console.log(`Marking ${itemIds.length} items as purchased:`, itemIds);
    
    // First validate the IDs
    if (!itemIds || !Array.isArray(itemIds) || itemIds.length === 0) {
      throw new Error('Invalid item IDs provided');
    }
    
    // Update exports table to mark items as purchased
    const { data, error } = await supabase
      .from('exports')
      .update({ is_purchased: true })
      .in('id', itemIds)
      .select();
      
    if (error) {
      console.error('Database error marking items as purchased:', error);
      throw new Error(`Database error: ${error.message}`);
    }
    
    console.log(`Successfully marked ${data?.length || 0} items as purchased`);
    
    return { 
      success: true, 
      updatedCount: data?.length || 0,
      updatedItems: data
    };
  } catch (error) {
    console.error('Error marking items as purchased:', error);
    return { error: error.message };
  }
}; 